import React, { useCallback, useEffect, useRef, useState } from "react";
import { Close } from "@mui/icons-material";
import JamileImage from "../static/jamileImage.jpg";
import PriyaImage from "../static/priya.jpg";
import NishthaImage from "../static/nishtha.jpg";
import UtkarshImage from "../static/utkarsh.jpg";
import LakshyaImage from "../static/lakshya.jpg";
import "./Testimonials.css";

const testimonials = [
  {
    name: "Lakshya Gupta",
    role: "Product Owner | Embodied AI & L4 Robotics | Ex-Founder",
    quote:
      "Aviral brings a calm, refined, and premium feel to every system he builds—a true reflection of his own personality.",
    fullQuote:
      "Aviral brings a calm, refined, and premium feel to every system he builds—a true reflection of his own personality.",
    context: "Managed Aviral directly · July 30, 2026",
    profileUrl: "https://www.linkedin.com/in/lakshyagupta10/",
    image: LakshyaImage,
  },
  {
    name: "Jamile Balli",
    role: "Program Coordinator, El Centro Latine Resource Center, CSU Chico",
    quote:
      "Aviral brings technical expertise, clear communication, and creative problem-solving to the teams he joins.",
    fullQuote:
      "Aviral is someone with a rare blend of technical expertise, strong communication skills, and creative problem-solving ability. As a student, he distinguished himself not only through his engineering aptitude but also as a gifted writer and poet in both English and his native language—an asset that enhances his clarity of thought, documentation, and collaboration. Aviral's work as a writing tutor highlighted his reliability, professionalism, clear communication with students, and commitment to helping others improve their academic and technical writing. These strengths translate directly to cross-functional teamwork, mentoring, and knowledge sharing in fast-paced environments. Focused, hardworking, and dependable, Aviral brings a thoughtful, supportive presence to teams while maintaining strong execution and attention to detail.",
    context: "Worked with Aviral at CSU Chico",
    image: JamileImage,
  },
  {
    name: "Priya Prakash",
    role: "AI Software Engineering, Intuit",
    quote:
      "His coding ability, patience, and communication make him a strong and thoughtful project partner.",
    fullQuote:
      "I am thrilled to recommend Aviral, who was my project and coding partner during college. I wholeheartedly vouch for his exceptional coding skills, problem-solving abilities, and patient approach to finding solutions. Aviral's communication skills are a game-changer, allowing him to easily connect and collaborate with others. Moreover, his insights are invaluable to every discussion which yields an efficient outcome. I have witnessed him working tirelessly on a variety of technologies and seen him grow. Any organization would be lucky to have him on board, conquering challenges and driving success.",
    context: "Project and coding partner at UPES",
    image: PriyaImage,
  },
  {
    name: "Nishtha Grover",
    role: "MTS, VMware",
    quote:
      "His dedication, timely communication, and quality of work make him an asset to every team.",
    fullQuote:
      "Aviral had been an integral part of Smart Analyzers - The Data Science Society of our college UPES. While working in the Editorial Core Committee, he showed utmost dedication and enthusiasm toward the work assigned to him. The timely response and quality of work done by him makes him an asset to any team he is in.",
    context: "Worked with Aviral at Smart Analyzers, UPES",
    image: NishthaImage,
  },
  {
    name: "Utkarsh Sandeep Singh",
    role: "Senior Consultant, EY",
    quote:
      "He pairs strong team management with care for the work entrusted to him.",
    fullQuote:
      "Aviral had been part of the Tech Team of our Data Science Society. Working as a team I can say that he has strong team-management qualities and is very concerned about the task assigned to him. All the best for his future endeavors.",
    context: "Worked with Aviral at Smart Analyzers, UPES",
    image: UtkarshImage,
  },
];

const Testimonials = () => {
  const [selected, setSelected] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const closeRef = useRef(null);
  const openerRef = useRef(null);
  const visibleTestimonials = showAll ? testimonials : testimonials.slice(0, 3);

  const closeSelected = useCallback(() => {
    setSelected(null);
    window.requestAnimationFrame(() => openerRef.current?.focus());
  }, []);

  useEffect(() => {
    if (!selected) return undefined;
    closeRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape") closeSelected();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [closeSelected, selected]);

  return (
    <section id="testimonials" className="portfolio-section testimonials-section" data-scene-section="testimonials">
      <div className="section-wrap" data-animate="true">
        <header className="destination-heading destination-heading--split">
          <div>
            <div className="destination-label"><span>06</span><span>Communication array</span></div>
            <h2 className="section-title">Signals from people I’ve worked with.</h2>
          </div>
          <p className="section-lede">Direct recommendations from managers, collaborators, and teammates across engineering, research, and academic work.</p>
        </header>
        <div className="testimonials-grid">
          {visibleTestimonials.map((testimonial, index) => (
            <figure key={testimonial.name} className={`testimonial-entry ${index === 0 ? "testimonial-entry--primary" : ""}`}>
              <figcaption>
                <img src={testimonial.image} alt={testimonial.name} loading="lazy" />
                <div>
                  <h3>
                    {testimonial.profileUrl ? (
                      <a href={testimonial.profileUrl} target="_blank" rel="noreferrer">
                        {testimonial.name}
                      </a>
                    ) : testimonial.name}
                  </h3>
                  <p>{testimonial.role}</p>
                  <p className="testimonial-context">{testimonial.context}</p>
                </div>
              </figcaption>
              <blockquote>{testimonial.quote}</blockquote>
              {testimonial.fullQuote !== testimonial.quote && (
                <button type="button" onClick={(event) => { openerRef.current = event.currentTarget; setSelected(testimonial); }}>Read full note</button>
              )}
            </figure>
          ))}
        </div>
        <button className="section-toggle" type="button" onClick={() => setShowAll((current) => !current)} aria-expanded={showAll}>
          {showAll ? "Show featured recommendations" : `View all ${testimonials.length} recommendations`}
        </button>
      </div>

      {selected && (
        <div className="testimonial-modal" role="dialog" aria-modal="true" aria-labelledby="testimonial-modal-title">
          <div className="testimonial-modal-scrim" onClick={closeSelected} />
          <article className="testimonial-modal-content">
            <button ref={closeRef} type="button" className="testimonial-modal-close" onClick={closeSelected} aria-label="Close note">
              <Close fontSize="small" />
            </button>
            <img src={selected.image} alt="" />
            <h2 id="testimonial-modal-title">{selected.name}</h2>
            <p className="testimonial-modal-role">{selected.role}</p>
            <p className="testimonial-modal-context">{selected.context}</p>
            <p>{selected.fullQuote}</p>
          </article>
        </div>
      )}
    </section>
  );
};

export default Testimonials;
